<template>
  <b-card class="card white-container">
    <multiselect
            class="currency-select"
            :searchable="false"
            v-if="isCurrency"
            v-model="curValue"
            @input="value => {createParamsObject('currency', value)}"
            :options="selectOptions"
            :preselect-first="true"
            :allow-empty="false"
            select-label=""
            selected-label=""
            deselect-label=""
    >
      <template slot="noResult">
        {{$t('global.no_results')}}
      </template>
    </multiselect>
    <div class="card-info">
      <div class="title-row">
      <span class="title-row__title">
        {{ title }}
      </span>
        <custom-tooltip
                class="title-row__tooltip"
                v-if="this.tooltipName && this.tooltipText"
                :name="this.tooltipName"
        >
          {{ $t(this.tooltipText) }}
        </custom-tooltip>
        <div v-if="percent !== false" :style="percentColor" class="title-row__percent">
          {{ percent }}%
        </div>
      </div>
      <multiselect
          class="plans-select retention-rate-plans"
          v-if="isRetentionRate"
          v-model="selectedPlans"
          :options="plansOptions"
          :group-values="'subscriptions'"
          :group-label="'title'"
          :group-select="true"
          :multiple="true"
          track-by="id"
          label="title"
          @input="updateChartData"
      >
        <template slot="noResult">
          {{$t('global.no_results')}}
        </template>
      </multiselect>
      <div class="total-row">
        <div class="total-row__current">{{ this.getCurrentValue() }}</div>
        <div class="total-row__previous" v-if="this.getCompareValue()" >{{ this.getCompareValue() }}</div>
      </div>
      <compare-chart-line
              :chart-id="tooltipName"
              :is-currency="isCurrency"
              :data="filteredChartData"
              :item-value-decorator="itemValueDecorator"
              :multiplier="getMultiplier()"
              :is-retention-rate="tooltipName === 'retention_rate'"
      />
    </div>
  </b-card>
</template>

<script>
import CompareChartLine from './CompareChartLine';
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { currency } from '../../mixins/currency';
import { format } from '../../mixins/format';

export default {
  name: 'StatisticsCard',
  mixins: [currency, format],
  components: {
    CompareChartLine,
  },
  props: {
    labels: {
      type: Array,
      default: null,
    },
    data: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    tooltipName: {
      type: String,
      default: '',
    },
    tooltipText: {
      type: String,
      default: '',
    },
    isCurrency: {
      type: Boolean,
      default: true,
    },
    itemValueDecorator: {
      type: Function,
      default: null,
    },
    currentValueDecorator: {
      type: Function,
      default: null,
    },
    comparedValueDecorator: {
      type: Function,
      default: null,
    },
    groupBy: {
      type: String,
      default: 'day',
    },
    plans: {
      type: Array,
      default: () => []
    },
    clearSelectedPlans: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      PERCENT_COLORS: {
        LOW: 'red',
        ZERO: '#656777',
        HIGH: '#04b82b',
      },
      defaultCurrencyVal: 'usd',
      curValue: 'usd',
      defaultCurrencies: ['usd'],
      selectedPlans: [],
    };
  },
  computed: {
    ...mapGetters({
      currenciesFromStore: 'currency/currency',
      currencyLoading: 'currency/loading',
      currentResource: 'resource/currentResource',
    }),
    isRetentionRate() {
      const result = this.tooltipName === 'retention_rate';
      return result;
    },
    percent() {
      if (this.data && this.data.change) return this.data.change;
      return false;
    },
    percentColor() {
      const percent = this.percent;
      let selectedColor = this.PERCENT_COLORS.ZERO;
      if (percent < 0) {
        selectedColor = this.PERCENT_COLORS.LOW;
      }
      if (percent > 0) {
        selectedColor = this.PERCENT_COLORS.HIGH;
      }
      return { color: `${selectedColor}`, ['border-color']: `${selectedColor}` };
    },
    chartLineData () {
      if ( !this.isCurrency ) {
        return this.data
      }
      const result = _.get(this.data, this.curValue.toLowerCase(), {});
      return result;
    },
    plansOptions() {
      const plans = this.$store.getters['data/plans'];
      return plans;
    },
    selectOptions() {
      if (!this.isCurrency) return this.defaultCurrencies;

      const availableCurrencies = _.filter(
          this.currenciesFromStore.map(currency => currency.value.toLowerCase()),
          currency => _.has(this.data, currency)
      );

      return !_.isEmpty(availableCurrencies)
          ? availableCurrencies
          : this.defaultCurrencies;
    },
    filteredChartData() {
      // if (!this.isRetentionRate || this.selectedPlans.length === 0) {
        return this.chartLineData;
      // }
      // return _.pickBy(this.chartLineData, (value, key) => {
      //   return this.selectedPlans.some(plan => plan.id === key);
      // });
    },
    chartCurrencies() {
      if( !this.isCurrency ) {
        return this.defaultCurrencies;
      }

      let currencies = _.filter(
              _.keys(this.data),
              (currency) => ( this.getCurrencies().includes(currency) )
      );

      return !_.isEmpty(currencies) ? currencies : this.defaultCurrencies;
    },
  },
  methods: {
    ...mapActions({
      fetchCurrencies: 'currency/getCurrency',
    }),
    updateChartData() {
      if (this.selectedPlans.length === 0) {
        this.$emit('refetchdata', null);
      } else {
        const planIds = this.selectedPlans.map(item => item.id).join(',');
        this.$emit('refetchdata', planIds);
      }
    },
    click() {
      this.percent++;
    },
    getCurrentValue() {
      let value = _.get(this.data, `current_value`, 0);
      if (this.isCurrency) {
        value = _.get(this.data, `${this.curValue.toLowerCase()}.current_value`, 0);
      }
      if (this.currentValueDecorator) {
        value = this.currentValueDecorator(value, this.getMultiplier());
      }
      return this.isCurrency
          ? `${this.getCurrencySign(this.curValue.toUpperCase())} ${this.formatAmount(value)}`
          : this.isRetentionRate
              ? `${value.toLocaleString()} percent`
              : value.toLocaleString();
    },
    getCompareValue() {
      if (!_.has(this.data, 'compare_value') && !this.isCurrency) return null;
      let value = _.get(this.data, 'compare_value', 0);
      if (this.isCurrency) {
        value = _.get(this.data, `${this.curValue.toLowerCase()}.compare_value`, 0);
      }
      if (this.comparedValueDecorator) {
        value = this.comparedValueDecorator(value, this.getMultiplier());
      }
      return this.isCurrency
          ? `${this.getCurrencySign(this.curValue.toUpperCase())} ${this.formatAmount(value)} previous period`
          : this.isRetentionRate
              ? `${value.toLocaleString()} previous period`
              : `${value.toLocaleString()} previous period`;
    },
    getMultiplier() {
      switch (this.groupBy) {
        case 'day':
          return 30;
        case 'week':
          return 4;
        case 'month':
          return 1;
        case 'year':
          return (1 / 12);
        default:
          return 1;
      }
    },
    getCurrencies() {
      return this.currenciesFromStore.map(currency => currency.value.toLowerCase());
    },
    createParamsObject(key, value) {
      this.$emit('update:currency', value);
    },
  },
  mounted() {
    if (_.isEmpty(this.currenciesFromStore) && !this.currencyLoading) {
      this.fetchCurrencies();
    }
  },
  watch: {
    curValue(newValue) {
      // console.log('curValue changed to:', newValue);
      this.$emit('currency-changed', newValue);
    },
    currentResource: {
      handler(newResource, oldResource) {
        if (newResource && oldResource && newResource.key !== oldResource.key) {
          this.selectedPlans = [];
          this.updateChartData();
        }
      },
      deep: true,
    },
    clearSelectedPlans: function () {
      this.selectedPlans = [];
    },
  },
};
</script>
<style lang="scss" scoped>
.card {
  padding: 0;
}
.title-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  &__title {
    font-size: 22px;
    font-weight: 500;
    color: #23282C;
  }

  &__tooltip {
    margin: 0 6px;
  }

  &__percent {
    border-radius: 4px;
    border: 1px solid;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 10px;
  }
}

.total-row {
  display: flex;
  align-items: center;
  margin-bottom: 7px;

  &__current {
    font-size: 14px;
    margin-right: 8px;
    color: #743b91;
    font-weight: 600;
  }

  &__previous {
    color: #656777;
    font-size: 13px;
  }
}
.retention-rate-plans {
  margin-bottom: 16px;
}
</style>
